import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../adminApi';
import AdminLayout from './AdminLayout';
import { STATUS_MAP, STATUS_FLOW, STATUS_BRANCHES } from '../../constants/projectStatus';

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtDate = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }); }
    catch { return val; }
};

// ステータスフロー凡例（案件がどんな順序で進むかを一覧表示）
const StatusFlowLegend = () => (
    <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e0e4ea', padding: '18px 24px', marginBottom: 24 }}>
        <h3 style={{ fontWeight: 700, color: '#1a1a2e', margin: '0 0 14px', fontSize: '0.95rem' }}>ステータスの流れ</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            {STATUS_FLOW.map((s, i) => (
                <div key={s.value} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 500, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
                        {s.step}. {s.label}
                    </span>
                    {i < STATUS_FLOW.length - 1 && <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>→</span>}
                </div>
            ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e0e4ea' }}>
            <span style={{ fontSize: '0.78rem', color: '#9ca3af', alignSelf: 'center' }}>その他:</span>
            {STATUS_BRANCHES.map(s => (
                <span key={s.value} style={{ padding: '4px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 500, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
                    {s.label}
                </span>
            ))}
        </div>
    </div>
);

const AdminDashboardPage = () => {
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get('admin/projects/summary'),
            api.get('admin/projects?filter=all'),
        ]).then(([s, p]) => {
            setSummary(s.data);
            setProjects(p.data.slice(0, 10));
        }).catch(console.error)
          .finally(() => setLoading(false));
    }, []);

    const SUMMARY_CARDS = summary ? [
        { label: '対応中の案件数',             value: `${summary.active_projects}件`,           accent: '#005c53' },
        { label: '今月の施工業者への振込待ち金額', value: `¥${fmt(summary.pending_payout_amount)}`, accent: '#f59e0b' },
        { label: '今月完了件数',               value: `${summary.completed_this_month}件`,       accent: '#10b981' },
        { label: '登録業者数',                 value: `${summary.total_contractors}社`,          accent: '#3b82f6' },
    ] : [];

    return (
        <AdminLayout>
            <div style={{ padding: '32px' }}>
                <div style={{ marginBottom: 32 }}>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>ダッシュボード</h2>
                    <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: '0.875rem' }}>システム全体の概要</p>
                </div>

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                        <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    </div>
                ) : (
                    <>
                        {/* サマリカード */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 32 }}>
                            {SUMMARY_CARDS.map((card, i) => (
                                <div key={i} style={{ background: '#fff', borderRadius: 8, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e0e4ea' }}>
                                    <div style={{ width: 44, height: 44, background: card.accent, borderRadius: 8, marginBottom: 14, opacity: 0.9 }} />
                                    <p style={{ color: '#6b7280', fontSize: '0.82rem', margin: '0 0 4px' }}>{card.label}</p>
                                    <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>{card.value}</p>
                                </div>
                            ))}
                        </div>

                        <StatusFlowLegend />

                        {/* 最近の案件テーブル */}
                        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e0e4ea' }}>
                            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e0e4ea' }}>
                                <h3 style={{ fontWeight: 700, color: '#1a1a2e', margin: 0, fontSize: '0.95rem' }}>最近の案件</h3>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                                    <thead>
                                        <tr style={{ background: '#f5f6f8', borderBottom: '1px solid #e0e4ea' }}>
                                            {['案件ID', '顧客名', '業者名', 'ステータス', '工事代金', '更新日'].map(h => (
                                                <th key={h} style={{ padding: '10px 20px', textAlign: 'left', color: '#6b7280', fontWeight: 500, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {projects.map(p => {
                                            const st = STATUS_MAP[p.status] || { label: p.status, bg: '#f3f4f6', color: '#6b7280' };
                                            const customer = p.customer ? `${p.customer.last_name} ${p.customer.first_name}` : '—';
                                            const amount = p.invoice?.amount ? `¥${fmt(p.invoice.amount)}` : '—';
                                            return (
                                                <tr key={p.id} style={{ borderBottom: '1px solid #e0e4ea', cursor: 'pointer' }}
                                                    onClick={() => navigate(`/admin/projects/${p.id}`)}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#f5f6f8'}
                                                    onMouseLeave={e => e.currentTarget.style.background = ''}
                                                >
                                                    <td style={{ padding: '14px 20px', color: '#005c53', fontWeight: 500, whiteSpace: 'nowrap' }}>#{p.id}</td>
                                                    <td style={{ padding: '14px 20px', color: '#1a1a2e', whiteSpace: 'nowrap' }}>{customer}</td>
                                                    <td style={{ padding: '14px 20px', color: '#1a1a2e', whiteSpace: 'nowrap' }}>{p.contractor?.company_name || '—'}</td>
                                                    <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                                                        <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 500, background: st.bg, color: st.color }}>
                                                            {st.label}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '14px 20px', color: '#1a1a2e', fontWeight: 500, whiteSpace: 'nowrap' }}>{amount}</td>
                                                    <td style={{ padding: '14px 20px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(p.updated_at)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </AdminLayout>
    );
};

export default AdminDashboardPage;
